<template>
  <Tooltip
    :enabled="account?.isBanned"
    :content="
      $t(
        'tuan-dui-xi-wei-bu-zu-nin-de-zhang-hao-zan-shi-wu-fa-deng-lu-qing-lian-xi-guan-li-yuan-jin-hang-xi-wei-jia-gou',
      )
    "
  >
    <div
      :class="[
        'account-item',
        { 'account-item--banned': account?.isBanned, 'account-item--active': isActive },
      ]"
      @click="$emit('click')"
    >
      <div class="account-item__left">
        <div class="account-item__avatar" v-if="account?.logoUrl">
          <Img class="account-item__avatar-img" :src="account?.logoUrl" alt="logo" />
          <div class="account-item__admin-mark" v-if="account?.isAdmin && isTeamRight">
            <IconPersonal class="icon" />
          </div>
        </div>
        <!-- 个人账号只展示name -->

        <div class="account-item__name">
          <span v-if="isPersonRight" class="team-name">{{ account?.accountName }}</span>
          <template v-else>
            <span class="team-name">{{ account?.teamName }}</span>
            <span class="account-item__account-name">({{ account?.accountName }})</span>
          </template>
        </div>
      </div>
      <div class="account-item__right">
        <template v-if="showVerifyTag">
          <Tag v-if="account?.isIdentify" variant="outline" color="success">
            {{ $t('yi-ren-zheng') }}
          </Tag>
          <Tag v-else-if="!account?.isIdentify && isVerifying" variant="outline" color="purple">
            {{ $t('ren-zheng-zhong') }}
          </Tag>
          <Tag v-else variant="outline">{{ $t('wei-ren-zheng') }}</Tag>
        </template>
        <RightTag v-if="account?.rightsType" :rights-type="account?.rightsType" />
      </div>
    </div>
  </Tooltip>
</template>

<script setup lang="ts">
import { PAGE_ACTION_KEYS } from '@rendora/common/entities/app_cfg';
import RightTag from '@rendora/common/features/right-tag/index.vue';
import { usePermissionStore } from '@rendora/common/store/permission';
import { IRightsType, USER_VERIFY_STATUS } from '@rendora/common/types/enums';
import { isUS } from '@rendora/common/utils/config';
import IconPersonal from '@rendora/design/assets/personal-fill.svg?component';
import { Img } from '@rendora/design/components/img/index';
import { Tag } from '@rendora/design/components/tag/index';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { computed } from 'vue';

const { hasActionPermission } = usePermissionStore();

const props = defineProps<Props>();

const showVerifyTag = computed(() => {
  return (
    isTeamRight.value &&
    !isUS() &&
    hasActionPermission(PAGE_ACTION_KEYS.CENTER_ACCOUNT_CHANGE_VERIFY_INFO)
  );
});

const isTeamRight = computed(() => {
  return [
    IRightsType.ORG_CUSTOM,
    IRightsType.ORG_STANDARD,
    IRightsType.ORG_FLAGSHIP,
    IRightsType.TEAM_LIGHT,
    IRightsType.TEAM_TASTE,
  ].includes(props.account?.rightsType || IRightsType.TASTE);
});

defineEmits<{ (e: 'click'): void }>();

const isPersonRight = computed(() => {
  return [IRightsType.PERSONAL, IRightsType.PERSONAL_LITE, IRightsType.TASTE].includes(
    props.account?.rightsType || IRightsType.TASTE,
  );
});

interface AccountInfo {
  isAdmin: boolean;
  teamName: string;
  accountName: string;
  logoUrl?: string;
  isIdentify?: boolean;
  identify_info?: Record<string, any>;
  rightsType?: IRightsType;
  isBanned?: boolean;
}

interface Props {
  account?: AccountInfo;
  isActive?: boolean;
}

const isVerifying = computed(() => {
  return props.account?.identify_info?.status === USER_VERIFY_STATUS.WAIT;
});
</script>

<style scoped lang="scss">
@use '@rendora/design/styles/mixins.scss' as *;

// 账号项主容器
.account-item {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: var(--x-radius-medium);
  background: transparent;
  width: 100%;

  @include mobile {
    padding: responsive-size(6px) responsive-size(12px);
  }
}

.account-item:hover:not(.account-item--banned):not(.account-item--active) {
  background: var(--x-back-4);
}

.account-item--banned {
  opacity: 0.5;
  cursor: not-allowed;
}

.account-item--active {
  cursor: default;
  background: var(--x-back-4);
}

// 左侧内容区域
.account-item__left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  @include mobile {
    gap: responsive-size(8px);
  }
}

// 右侧内容区域
.account-item__right {
  display: flex;
  align-items: center;
  gap: 13px;
  flex-shrink: 0;

  @include mobile {
    gap: responsive-size(13px);
  }
}

// 头像容器
.account-item__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  position: relative;

  @include mobile {
    width: responsive-size(28px);
    height: responsive-size(28px);
  }
}

.account-item__avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}

.account-item__admin-mark {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--x-warning-6);
  display: flex;
  align-items: center;
  color: var(--x-white-1);
  justify-content: center;

  @include mobile {
    width: responsive-size(14px);
    height: responsive-size(14px);
  }
}

.account-item__admin-mark .icon {
  width: 8px;

  @include mobile {
    width: responsive-size(8px);
  }
}

// 名称区域
.account-item__name {
  flex: 1;
  min-width: 0;
  font-size: var(--x-text-14-size);
  line-height: var(--x-text-14-line-height);
  color: var(--x-text-1);
  white-space: nowrap;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-item__account-name {
  color: var(--x-text-7);
  margin-left: 4px;

  @include mobile {
    margin-left: responsive-size(4px);
  }
}
</style>
